import { useEffect, useRef, useState } from "react";
import { useI18n } from "@/i18n/I18nProvider";

/**
 * Language switcher.
 *
 * Desktop (>= 901px): shows the three-pill toggle inline (IT | EN | FR).
 * Mobile  (<= 900px): shows a compact "globe + current lang code" button
 *                     that opens a small dropdown menu on tap. This avoids
 *                     the wider pill overlapping page content when floating.
 */
const LABELS = { it: "IT", en: "EN", fr: "FR" };
const FLAGS = { it: "🇮🇹", en: "🇬🇧", fr: "🇫🇷" };
const FULL_NAMES = { it: "Italiano", en: "English", fr: "Français" };

export default function LangSwitch({ compact = false }) {
    const { lang, setLang, t } = useI18n();
    const [open, setOpen] = useState(false);
    const wrapRef = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const handler = (e) => {
            if (wrapRef.current && !wrapRef.current.contains(e.target)) {
                setOpen(false);
            }
        };
        document.addEventListener("mousedown", handler);
        document.addEventListener("touchstart", handler);
        return () => {
            document.removeEventListener("mousedown", handler);
            document.removeEventListener("touchstart", handler);
        };
    }, [open]);

    /* ---------- Compact / mobile dropdown ---------- */
    if (compact) {
        return (
            <div
                ref={wrapRef}
                data-testid="lang-switch"
                style={{ position: "relative" }}
            >
                <button
                    type="button"
                    onClick={() => setOpen((v) => !v)}
                    aria-haspopup="menu"
                    aria-expanded={open}
                    aria-label={t("lang.switchTo")}
                    data-testid="lang-switch-toggle"
                    style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 6,
                        padding: "6px 10px",
                        borderRadius: 999,
                        background: "var(--itly-teal-900)",
                        color: "var(--itly-on-teal)",
                        border: "none",
                        cursor: "pointer",
                        fontFamily: "Inter, sans-serif",
                        fontSize: 12,
                        fontWeight: 700,
                        letterSpacing: "0.06em",
                        boxShadow: "0 4px 12px rgba(15,61,62,0.18)",
                    }}
                >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                        <circle cx="12" cy="12" r="10" />
                        <path d="M2 12h20M12 2a15 15 0 0 1 0 20M12 2a15 15 0 0 0 0 20" />
                    </svg>
                    <span aria-hidden="true" style={{ fontSize: 14, lineHeight: 1 }}>{FLAGS[lang] || FLAGS.it}</span>
                    {LABELS[lang] || "IT"}
                    <span aria-hidden="true" style={{ fontSize: 9 }}>▾</span>
                </button>
                {open && (
                    <div
                        role="menu"
                        data-testid="lang-switch-menu"
                        style={{
                            position: "absolute",
                            top: "calc(100% + 6px)",
                            right: 0,
                            background: "var(--itly-surface)",
                            border: "1px solid var(--itly-border)",
                            borderRadius: 12,
                            boxShadow: "0 8px 24px rgba(15,61,62,0.16)",
                            padding: 4,
                            minWidth: 96,
                            display: "flex",
                            flexDirection: "column",
                        }}
                    >
                        {["it", "en", "fr"].map((code) => (
                            <button
                                key={code}
                                type="button"
                                onClick={() => { setLang(code); setOpen(false); }}
                                data-testid={`lang-btn-${code}`}
                                aria-pressed={lang === code}
                                style={{
                                    padding: "8px 10px",
                                    borderRadius: 8,
                                    border: "none",
                                    background: lang === code ? "var(--itly-teal-050)" : "transparent",
                                    color: "var(--itly-fg-strong)",
                                    fontFamily: "Inter, sans-serif",
                                    fontSize: 13,
                                    fontWeight: lang === code ? 700 : 500,
                                    letterSpacing: "0.01em",
                                    cursor: "pointer",
                                    textAlign: "left",
                                    display: "flex",
                                    alignItems: "center",
                                    gap: 10,
                                }}
                            >
                                <span aria-hidden="true" style={{ fontSize: 16, lineHeight: 1 }}>{FLAGS[code]}</span>
                                <span style={{ flex: 1 }}>{FULL_NAMES[code]}</span>
                                {lang === code && (
                                    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><path d="M5 12l5 5L20 7" /></svg>
                                )}
                            </button>
                        ))}
                    </div>
                )}
            </div>
        );
    }

    /* ---------- Desktop inline pill ---------- */
    return (
        <div
            role="group"
            aria-label={t("lang.switchTo")}
            data-testid="lang-switch"
            style={{
                display: "inline-flex",
                alignItems: "center",
                gap: 2,
                padding: 3,
                borderRadius: 999,
                background: "rgba(15, 61, 62, 0.06)",
                border: "1px solid rgba(15, 61, 62, 0.14)",
            }}
        >
            {["it", "en", "fr"].map((code) => (
                <button
                    key={code}
                    type="button"
                    onClick={() => setLang(code)}
                    data-testid={`lang-btn-${code}`}
                    aria-pressed={lang === code}
                    title={FULL_NAMES[code]}
                    style={{
                        padding: "5px 10px",
                        borderRadius: 999,
                        border: "none",
                        background: lang === code ? "var(--itly-teal-900)" : "transparent",
                        color: lang === code ? "var(--itly-on-teal)" : "var(--itly-fg-strong)",
                        fontFamily: "Inter, sans-serif",
                        fontSize: 11.5,
                        fontWeight: 700,
                        letterSpacing: "0.06em",
                        cursor: "pointer",
                        transition: "background 180ms ease, color 180ms ease",
                        textTransform: "uppercase",
                        display: "inline-flex",
                        alignItems: "center",
                        gap: 5,
                    }}
                >
                    <span aria-hidden="true" style={{ fontSize: 12, lineHeight: 1 }}>{FLAGS[code]}</span>
                    {code}
                </button>
            ))}
        </div>
    );
}
